import { StrategyBuilderFunction, EMPTY_STRATEGY } from './types';
import { addLongPut, addShortPut } from './addOption';
import {
  getDefaultExpiration,
  findClosestStrikePrice,
  getStrikesAtExpiration,
  expirationToDate,
  getPremium,
  getStrategyLegStrikeDirection,
  getStrikeDistance,
} from './utils';

export const putRatioBackspread: StrategyBuilderFunction = ({
  chain,
  spot,
  expirationDate,
  expirationKey,
  strikeDirection,
  contractMultiplier = 1,
}) => {
  const strikesAtExpiration = expirationKey
    ? getStrikesAtExpiration(chain, expirationKey)
    : getDefaultExpiration(chain);

  if (
    !strikesAtExpiration ||
    !strikesAtExpiration.e ||
    !strikesAtExpiration.ey ||
    !strikesAtExpiration.em ||
    !strikesAtExpiration.ed
  ) {
    return EMPTY_STRATEGY;
  }

  const expirationDateForOptions =
    expirationDate ||
    expirationToDate(strikesAtExpiration.ey, strikesAtExpiration.em, strikesAtExpiration.ed);
  const expirationKeyForOptions = expirationKey || strikesAtExpiration.e;

  // Selecting strike prices for the strategy
  const buyPutStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: -2 }),
  ); // Buying further OTM puts
  const sellPutStrike = findClosestStrikePrice(strikesAtExpiration, spot); // Selling NTM or ATM puts

  // Determining the number of contracts for the ratio
  const sellContracts = 1 * contractMultiplier; // Selling fewer puts
  const buyContracts = 2 * contractMultiplier; // Buying more puts, establishing a backspread

  return {
    optionPositions: [
      // Sell NTM or ATM put options
      addShortPut({
        strike: sellPutStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[sellPutStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[sellPutStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: sellContracts,
      }),
      // Buy further OTM put options
      addLongPut({
        strike: buyPutStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[buyPutStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[buyPutStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: buyContracts,
      }),
    ],
    stockPositions: [],
  };
};
